import { useState, useEffect } from 'react';
import { useOnboardingStore, getStepNumber, getTotalSteps } from '../../store/onboardingStore';
import { useWorkHoursStore } from '../../store/workHoursStore';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui';
import { Modal } from '../ui/Modal';
import { googleContactsAPI } from '../../services/api';

export function OnboardingModal() {
  const {
    isOpen,
    currentStep,
    data,
    nextStep,
    prevStep,
    setData,
    complete,
    dismiss,
  } = useOnboardingStore();

  const stepNumber = getStepNumber(currentStep);
  const totalSteps = getTotalSteps();

  // Handle Escape key to dismiss modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        dismiss();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, dismiss]);

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={dismiss} size="2xl">
      <div className="p-6">
        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close onboarding"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Progress indicator */}
        {currentStep !== 'welcome' && currentStep !== 'complete' && (
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
              <span>Step {stepNumber} of {totalSteps}</span>
              <span>{Math.round((stepNumber / totalSteps) * 100)}% complete</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-ondeck-500 transition-all duration-300"
                style={{ width: `${(stepNumber / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Step content */}
        {currentStep === 'welcome' && <WelcomeStep onNext={nextStep} onSkip={dismiss} />}
        {currentStep === 'profile' && (
          <ProfileStep
            data={data}
            setData={setData}
            onNext={nextStep}
            onBack={prevStep}
          />
        )}
        {currentStep === 'import_contacts' && (
          <ImportContactsStep
            setData={setData}
            onNext={nextStep}
            onBack={prevStep}
          />
        )}
        {currentStep === 'work_hours' && (
          <WorkHoursStep onNext={nextStep} onBack={prevStep} />
        )}
        {currentStep === 'preferences' && (
          <PreferencesStep onNext={nextStep} onBack={prevStep} />
        )}
        {currentStep === 'complete' && (
          <CompleteStep onFinish={complete} />
        )}
      </div>
    </Modal>
  );
}

// Step Components

function WelcomeStep({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  const { user } = useAuthStore();
  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="text-center py-8">
      <div className="text-6xl mb-6">⚾</div>
      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
        Welcome to OnDeck, {firstName}!
      </h2>
      <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
        Let's get you set up in just a few minutes. We'll help you import your contacts
        and configure your preferences.
      </p>

      <div className="flex flex-col gap-4 items-center">
        <Button onClick={onNext} size="lg" className="px-8">
          Let's Get Started
        </Button>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This will only take about 2 minutes
        </p>
        <button
          onClick={onSkip}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mt-2"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

function ProfileStep({
  data,
  setData,
  onNext,
  onBack,
}: {
  data: { displayName?: string; jobTitle?: string };
  setData: (data: { displayName?: string; jobTitle?: string }) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const { user } = useAuthStore();
  const [displayName, setDisplayName] = useState(data.displayName || user?.name || '');
  const [jobTitle, setJobTitle] = useState(data.jobTitle || '');

  const handleNext = () => {
    setData({ displayName, jobTitle });
    onNext();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Tell us about yourself
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        This helps personalize your experience.
      </p>

      <div className="space-y-4 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Display Name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="How should we address you?"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Job Title (optional)
          </label>
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g., Real Estate Agent, Broker"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
          />
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleNext} disabled={!displayName.trim()}>
          Continue
        </Button>
      </div>
    </div>
  );
}

function ImportContactsStep({
  setData,
  onNext,
  onBack,
}: {
  setData: (data: { importSource: 'google' | 'csv' | 'skip'; contactsImported?: number }) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    duplicates: number;
  } | null>(null);

  const handleGoogleImport = async () => {
    setIsImporting(true);
    setImportError(null);

    try {
      const result = await googleContactsAPI.import();

      if (result.success && result.data) {
        setImportResult({
          imported: result.data.imported,
          skipped: result.data.skipped,
          duplicates: result.data.duplicates,
        });
        setData({ importSource: 'google', contactsImported: result.data.imported });
      } else {
        // Handle specific error codes
        const errorCode = result.error?.code;
        let errorMessage = result.error?.message || 'Failed to import contacts';

        if (errorCode === 'NO_GOOGLE_CONNECTION') {
          errorMessage = 'Please sign in with Google to import contacts.';
        } else if (errorCode === 'MISSING_SCOPE') {
          errorMessage = 'Google Contacts permission not granted. Please sign out and sign in again to grant access.';
        } else if (errorCode === 'IMPORT_IN_PROGRESS') {
          errorMessage = 'An import is already in progress. Please wait a moment and try again.';
        }

        setImportError(errorMessage);
      }
    } catch (error) {
      console.error('Google import error:', error);
      setImportError('An unexpected error occurred. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCSVImport = () => {
    // The CSVImportModal will handle the actual import
    // For now, just mark as selected and continue
    setData({ importSource: 'csv' });
    onNext();
  };

  const handleSkip = () => {
    setData({ importSource: 'skip' });
    onNext();
  };

  const handleContinueAfterImport = () => {
    onNext();
  };

  // Show success state after import
  if (importResult) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Import Complete!
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Your Google Contacts have been imported successfully.
        </p>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-green-800 dark:text-green-300">
                {importResult.imported} contact{importResult.imported !== 1 ? 's' : ''} imported
              </h3>
            </div>
          </div>

          <div className="space-y-2 text-sm text-green-700 dark:text-green-400">
            {importResult.skipped > 0 && (
              <p>{importResult.skipped} skipped (missing name or contact info)</p>
            )}
            {importResult.duplicates > 0 && (
              <p>{importResult.duplicates} duplicate{importResult.duplicates !== 1 ? 's' : ''} found</p>
            )}
          </div>
        </div>

        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            Back
          </Button>
          <Button onClick={handleContinueAfterImport}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Import your contacts
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Bring your existing contacts into OnDeck to get started quickly.
      </p>

      {/* Error message */}
      {importError && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-red-700 dark:text-red-300">{importError}</p>
              <button
                onClick={() => setImportError(null)}
                className="text-sm text-red-600 dark:text-red-400 hover:underline mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4 mb-8">
        {/* Google Contacts Option */}
        <button
          onClick={handleGoogleImport}
          disabled={isImporting}
          className={`w-full flex items-center gap-4 p-4 border-2 rounded-xl transition-colors text-left ${
            isImporting
              ? 'border-ondeck-500 bg-ondeck-50 dark:bg-ondeck-900/20 cursor-wait'
              : 'border-gray-200 dark:border-gray-600 hover:border-ondeck-500 dark:hover:border-ondeck-500'
          }`}
        >
          <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center">
            {isImporting ? (
              <svg className="w-6 h-6 text-ondeck-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-8 h-8">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {isImporting ? 'Importing from Google Contacts...' : 'Import from Google Contacts'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isImporting ? 'This may take a moment' : 'Automatically sync your contacts from Google'}
            </p>
          </div>
          {!isImporting && (
            <span className="text-ondeck-500 text-sm font-medium">Recommended</span>
          )}
        </button>

        {/* CSV Option */}
        <button
          onClick={handleCSVImport}
          disabled={isImporting}
          className={`w-full flex items-center gap-4 p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl transition-colors text-left ${
            isImporting ? 'opacity-50 cursor-not-allowed' : 'hover:border-ondeck-500 dark:hover:border-ondeck-500'
          }`}
        >
          <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Upload CSV File
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Import from a spreadsheet or other CRM export
            </p>
          </div>
        </button>
      </div>

      <div className="flex justify-between items-center">
        <Button variant="ghost" onClick={onBack} disabled={isImporting}>
          Back
        </Button>
        <button
          onClick={handleSkip}
          disabled={isImporting}
          className={`text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 ${
            isImporting ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

function WorkHoursStep({
  onNext,
  onBack,
}: {
  onNext: () => void;
  onBack: () => void;
}) {
  const { workHours, setWorkHours } = useWorkHoursStore();
  const [startHour, setStartHour] = useState(workHours.startHour);
  const [endHour, setEndHour] = useState(workHours.endHour);
  const [workDays, setLocalWorkDays] = useState<number[]>(workHours.workDays);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const toggleDay = (day: number) => {
    if (workDays.includes(day)) {
      setLocalWorkDays(workDays.filter((d) => d !== day));
    } else {
      setLocalWorkDays([...workDays, day].sort());
    }
  };

  const handleNext = () => {
    setWorkHours({ startHour, startMinute: 0, endHour, endMinute: 0, workDays });
    onNext();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Set your work hours
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        We'll time your daily lineup and reminders to match your schedule.
      </p>

      <div className="space-y-6 mb-8">
        {/* Time Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Time
            </label>
            <select
              value={startHour}
              onChange={(e) => setStartHour(parseInt(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Time
            </label>
            <select
              value={endHour}
              onChange={(e) => setEndHour(parseInt(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Work Days */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Work Days
          </label>
          <div className="flex gap-2">
            {dayNames.map((name, index) => (
              <button
                key={index}
                onClick={() => toggleDay(index)}
                className={`
                  w-12 h-12 rounded-lg font-medium text-sm transition-colors
                  ${workDays.includes(index)
                    ? 'bg-ondeck-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }
                `}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleNext}>
          Continue
        </Button>
      </div>
    </div>
  );
}

function PreferencesStep({
  onNext,
  onBack,
}: {
  onNext: () => void;
  onBack: () => void;
}) {
  const [enableAI, setEnableAI] = useState(true);
  const [enableNotifications, setEnableNotifications] = useState(true);

  const handleNext = () => {
    // Save preferences to localStorage
    localStorage.setItem('ondeck-ai-enabled', JSON.stringify(enableAI));
    localStorage.setItem('ondeck-notifications-enabled', JSON.stringify(enableNotifications));
    onNext();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Customize your experience
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Configure how OnDeck works for you.
      </p>

      <div className="space-y-4 mb-8">
        {/* AI Features Toggle */}
        <label className="flex items-start gap-4 p-4 border border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <input
            type="checkbox"
            checked={enableAI}
            onChange={(e) => setEnableAI(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-ondeck-600 focus:ring-ondeck-500"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white">
                Enable AI Features
              </span>
              <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
                Recommended
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Get AI-powered daily lineups, smart contact suggestions, and message drafting
            </p>
          </div>
        </label>

        {/* Notifications Toggle */}
        <label className="flex items-start gap-4 p-4 border border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <input
            type="checkbox"
            checked={enableNotifications}
            onChange={(e) => setEnableNotifications(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-ondeck-600 focus:ring-ondeck-500"
          />
          <div className="flex-1">
            <span className="font-medium text-gray-900 dark:text-white">
              Daily Lineup Notifications
            </span>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Receive your daily contact lineup via email and push notifications
            </p>
          </div>
        </label>
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleNext}>
          Continue
        </Button>
      </div>
    </div>
  );
}

function CompleteStep({ onFinish }: { onFinish: () => void }) {
  return (
    <div className="text-center py-8">
      <div className="text-6xl mb-6">🎉</div>
      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
        You're all set!
      </h2>
      <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
        Your OnDeck account is ready to go. Start managing your contacts and
        building stronger relationships.
      </p>

      <div className="bg-ondeck-50 dark:bg-ondeck-900/20 border border-ondeck-200 dark:border-ondeck-800 rounded-xl p-6 mb-8 max-w-md mx-auto">
        <h3 className="font-semibold text-ondeck-800 dark:text-ondeck-300 mb-3">
          Quick Tips
        </h3>
        <ul className="text-sm text-ondeck-700 dark:text-ondeck-400 space-y-2 text-left">
          <li className="flex items-start gap-2">
            <span>⚾</span>
            <span>Use the Sorting Game to organize contacts into stages</span>
          </li>
          <li className="flex items-start gap-2">
            <span>📊</span>
            <span>Check your Dashboard for today's lineup</span>
          </li>
          <li className="flex items-start gap-2">
            <span>⚙️</span>
            <span>Visit Settings anytime to adjust preferences</span>
          </li>
        </ul>
      </div>

      <Button onClick={onFinish} size="lg" className="px-8">
        Go to Dashboard
      </Button>
    </div>
  );
}
